import { QRCodeSVG } from 'qrcode.react';
import type { TemplateData } from './template-types';
import { TIPO_LABEL, SECCION_LABEL } from './template-types';

const BLUE = '#1a56db';

function QRBox({ url }: { url?: string }) {
  if (url) {
    return (
      <div style={{ width: 80, height: 80, border: '2px solid #999', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <QRCodeSVG value={url} size={72} />
      </div>
    );
  }
  return (
    <div style={{ width: 80, height: 80, border: '2px solid #999', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg viewBox="0 0 21 21" width="72" height="72">
        {/* QR placeholder pattern */}
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => {
          const pattern = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]];
          const val = r < 7 && c < 7 ? pattern[r]?.[c] : (((r * 3 + c * 7) % 3) === 0 ? 1 : 0);
          return val ? <rect key={`${r}-${c}`} x={c * 3} y={r * 3} width={3} height={3} fill="#222" /> : null;
        }))}
        {/* Bottom-left finder */}
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => {
          const pattern = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]];
          const val = pattern[r]?.[c];
          return val ? <rect key={`bl-${r}-${c}`} x={c * 3} y={(14 + r) * 3} width={3} height={3} fill="#222" /> : null;
        }))}
        {/* Top-right finder */}
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => {
          const pattern = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]];
          const val = pattern[r]?.[c];
          return val ? <rect key={`tr-${r}-${c}`} x={(14 + c) * 3} y={r * 3} width={3} height={3} fill="#222" /> : null;
        }))}
      </svg>
    </div>
  );
}

function Caduceus() {
  return (
    <svg viewBox="0 0 100 180" style={{ position: 'absolute', width: 180, height: 280, opacity: 0.055, left: '50%', top: '38%', transform: 'translateX(-50%) translateY(-50%)', pointerEvents: 'none' }} fill={BLUE}>
      <rect x="48" y="0" width="4" height="180" />
      <ellipse cx="50" cy="22" rx="28" ry="14" fill="none" stroke={BLUE} strokeWidth="4" />
      <path d="M50,40 Q30,55 50,70 Q70,85 50,100 Q30,115 50,130 Q70,145 50,160" fill="none" stroke={BLUE} strokeWidth="4.5" strokeLinecap="round" />
      <path d="M50,40 Q70,55 50,70 Q30,85 50,100 Q70,115 50,130 Q30,145 50,160" fill="none" stroke={BLUE} strokeWidth="4.5" strokeLinecap="round" />
    </svg>
  );
}

function Contenido({ data }: { data: TemplateData }) {
  if (data.tipo === 'receta') return (
    <>
      {data.medicaciones?.map((m, i) => (
        <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontWeight: 700, fontSize: 12 }}>{i + 1}. {m.denominacionGenerica}{m.denominacionDistintiva ? <span style={{ fontWeight: 400, color: '#666', fontSize: 11 }}> ({m.denominacionDistintiva})</span> : ''}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 2, fontSize: 11, color: '#444' }}>
            {m.dosis             && <span>Dosis: <b>{m.dosis}</b></span>}
            {m.frecuencia        && <span>Frecuencia: <b>{m.frecuencia}</b></span>}
            {m.duracion          && <span>Duración: <b>{m.duracion}</b></span>}
            {m.viaAdministracion && <span>Vía: <b>{m.viaAdministracion}</b></span>}
          </div>
          {m.indicaciones && <div style={{ fontSize: 10.5, color: '#666', marginTop: 2, fontStyle: 'italic' }}>{m.indicaciones}</div>}
        </div>
      ))}
      {(!data.medicaciones?.length) && <p style={{ color: '#999', fontStyle: 'italic', fontSize: 11 }}>No se incluyeron medicamentos.</p>}
    </>
  );
  if (data.tipo === 'orden') return (
    <div style={{ fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
      {data.instrucciones || <span style={{ color: '#999', fontStyle: 'italic' }}>Sin indicaciones.</span>}
    </div>
  );
  if (data.tipo === 'presupuesto') {
    const items    = data.presupuestoItems ?? [];
    const moneda   = data.presupuestoMoneda ?? 'MXN';
    const subtotal = items.reduce((s, i) => s + i.cantidad * i.precioUnitario, 0);
    const descPct  = data.presupuestoDescuento ?? 0;
    const descAmt  = subtotal * (descPct / 100);
    const ivaPct   = data.presupuestoIva ?? 0;
    const ivaAmt   = (subtotal - descAmt) * (ivaPct / 100);
    const total    = subtotal - descAmt + ivaAmt;
    const fmt      = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 12px', padding: '5px 4px', background: '#f3f4f6', fontSize: 10, fontWeight: 700, color: '#555', borderTop: '1px solid #d1d5db', borderBottom: '1px solid #d1d5db' }}>
          <span>Servicio</span>
          <span style={{ textAlign: 'right', minWidth: 28 }}>Cant.</span>
          <span style={{ textAlign: 'right', minWidth: 72 }}>Precio u.</span>
          <span style={{ textAlign: 'right', minWidth: 80 }}>Total</span>
        </div>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 12px', padding: '7px 4px', borderBottom: '1px solid #f0f0f0', fontSize: 11, alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{item.servicio}</div>
              {item.descripcion && <div style={{ fontSize: 9.5, color: '#888' }}>{item.descripcion}</div>}
            </div>
            <span style={{ textAlign: 'right', color: '#555', minWidth: 28 }}>{item.cantidad}</span>
            <span style={{ textAlign: 'right', minWidth: 72 }}>{fmt(item.precioUnitario)}</span>
            <span style={{ textAlign: 'right', fontWeight: 600, minWidth: 80 }}>{fmt(item.cantidad * item.precioUnitario)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, marginTop: 10, fontSize: 11 }}>
          <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#777' }}>Subtotal</span><span style={{ minWidth: 80, textAlign: 'right' }}>{fmt(subtotal)}</span></div>
          {descPct > 0 && <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#777' }}>Descuento ({descPct}%)</span><span style={{ minWidth: 80, textAlign: 'right', color: '#16a34a' }}>−{fmt(descAmt)}</span></div>}
          {ivaPct > 0  && <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#777' }}>IVA ({ivaPct}%)</span><span style={{ minWidth: 80, textAlign: 'right' }}>{fmt(ivaAmt)}</span></div>}
          <div style={{ display: 'flex', gap: 16, borderTop: `2px solid ${BLUE}`, paddingTop: 5, marginTop: 2 }}>
            <span style={{ fontWeight: 700 }}>TOTAL {moneda}</span>
            <span style={{ minWidth: 80, textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(total)}</span>
          </div>
          {data.presupuestoValidezDias && <div style={{ fontSize: 9.5, color: '#888', marginTop: 4 }}>Vigencia: {data.presupuestoValidezDias} días</div>}
        </div>
      </div>
    );
  }
  if (data.tipo === 'plan-tratamiento') {
    const items = data.planTratamientoItems ?? [];
    return (
      <div>
        {(data.diagnostico || data.objetivoTratamiento) && (
          <div style={{ background: '#f3f4f6', borderLeft: `3px solid ${BLUE}`, padding: '7px 10px', marginBottom: 10, fontSize: 11 }}>
            {data.diagnostico        && <div><b>Diagnóstico:</b> {data.diagnostico}</div>}
            {data.objetivoTratamiento && <div style={{ marginTop: 3 }}><b>Objetivo:</b> {data.objetivoTratamiento}</div>}
          </div>
        )}
        {items.map((item, i) => (
          <div key={i} style={{ padding: '7px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 700, fontSize: 12 }}>{i + 1}. {item.tratamiento}</div>
            {item.descripcion && <div style={{ fontSize: 10.5, color: '#555', marginTop: 2 }}>{item.descripcion}</div>}
            <div style={{ display: 'flex', gap: 20, marginTop: 3, fontSize: 10, color: '#777' }}>
              {item.sesiones !== undefined && <span>Sesiones: <b>{item.sesiones}</b></span>}
              {item.duracion               && <span>Duración: <b>{item.duracion}</b></span>}
            </div>
          </div>
        ))}
        {(data.duracionTratamiento || data.frecuenciaTratamiento) && (
          <div style={{ marginTop: 10, padding: '7px 10px', background: '#eff6ff', borderRadius: 4, fontSize: 10.5, color: '#1e40af' }}>
            {data.duracionTratamiento  && <span><b>Duración total:</b> {data.duracionTratamiento}</span>}
            {data.duracionTratamiento && data.frecuenciaTratamiento && <span style={{ margin: '0 10px', opacity: 0.4 }}>│</span>}
            {data.frecuenciaTratamiento && <span><b>Frecuencia:</b> {data.frecuenciaTratamiento}</span>}
          </div>
        )}
      </div>
    );
  }
  if (data.tipo === 'consentimiento') return (
    <div>
      {data.consentimientoTitulo && <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 8 }}>{data.consentimientoTitulo}</div>}
      <div style={{ fontSize: 11.5, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
        {data.consentimientoContenido || <span style={{ color: '#999', fontStyle: 'italic' }}>Sin contenido.</span>}
      </div>
    </div>
  );
  return (
    <>
      {data.estudios?.map((e, i) => (
        <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontWeight: 700 }}>{i + 1}. {e.nombre}</div>
          {e.descripcion && <div style={{ fontSize: 10.5, color: '#666' }}>{e.descripcion}</div>}
        </div>
      ))}
    </>
  );
}

function FirmaPacienteBox({ data }: { data: TemplateData }) {
  if (data.tipo !== 'consentimiento') return null;
  return (
    <div style={{ textAlign: 'center' }}>
      {data.firmaUrl
        ? (data.firmaEsPdf
          ? <div style={{ fontSize: 9.5, color: '#555', fontStyle: 'italic', width: 180 }}>Documento firmado adjunto (PDF)</div>
          : <img src={data.firmaUrl} alt="Firma del paciente" style={{ height: 50, maxWidth: 180, objectFit: 'contain' }} />)
        : <div style={{ height: 50, width: 180 }} />}
      <div style={{ borderTop: '1.5px solid #333', width: 180, marginTop: 4, marginBottom: 4 }} />
      <div style={{ fontSize: 10, color: '#555' }}>{data.paciente.nombre}</div>
      <div style={{ fontSize: 9.5, color: '#777' }}>Firma del paciente</div>
    </div>
  );
}

export function TemplateClasico({ data, width = 794, height = 1123 }: { data: TemplateData; width?: number; height?: number }) {
  const tituloDoc = TIPO_LABEL[data.tipo];
  return (
    <div style={{ width, height, fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 11, color: '#1a1a1a', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', position: 'relative', boxSizing: 'border-box', padding: '28px 40px 24px' }}>
      <Caduceus />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
        {/* Logo */}
        <div style={{ width: 60, height: 60, background: '#e8f0fe', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {data.clinica.logoUrl
            ? <img src={data.clinica.logoUrl} alt="logo" style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 6 }} />
            : <span style={{ fontSize: 18, fontWeight: 900, color: BLUE }}>{data.clinica.nombre.charAt(0)}</span>}
        </div>

        {/* Clinic info */}
        <div style={{ textAlign: 'center', flex: 1, padding: '0 24px' }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: BLUE, textTransform: 'uppercase', letterSpacing: 0.5 }}>{data.clinica.nombre}</div>
          {data.clinica.cedula && <div style={{ fontSize: 10.5, color: '#555', marginTop: 2 }}>{data.clinica.cedula}</div>}
          <div style={{ fontSize: 10, color: '#666', marginTop: 1 }}>{data.medico.nombre}</div>
          {data.medico.especialidad && <div style={{ fontSize: 10, color: '#888' }}>{data.medico.especialidad}</div>}
        </div>

        {/* Document meta */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 12 }}>{tituloDoc}</div>
          {data.generadoPor && <div style={{ fontSize: 9.5, color: '#777', marginTop: 2 }}>Generado por: <b>{data.generadoPor}</b></div>}
          {data.impresoPor  && <div style={{ fontSize: 9.5, color: '#777' }}>Impreso por: {data.impresoPor}</div>}
          <div style={{ fontSize: 9.5, color: '#777' }}>Fecha: {data.fecha}</div>
          <div style={{ fontSize: 11, color: '#e02020', fontWeight: 700, marginTop: 2 }}>Folio: {data.folio}</div>
        </div>
      </div>

      {/* Double blue lines */}
      <div style={{ borderTop: `3px solid ${BLUE}`, marginBottom: 3 }} />
      <div style={{ borderTop: `1.5px solid ${BLUE}`, marginBottom: 10 }} />

      {/* Patient info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 32, rowGap: 4, fontSize: 11 }}>
        <div><b>Paciente:</b> {data.paciente.nombre.toUpperCase()}</div>
        <div style={{ textAlign: 'right' }}><b>Fecha de Nacimiento:</b> {data.paciente.fechaNacimiento ?? '—'}</div>
        <div><b>Sexo:</b> {data.paciente.sexo ?? ''}</div>
        <div style={{ textAlign: 'right' }}><b>Fecha de consulta:</b> {data.fechaConsulta ?? '—'}</div>
      </div>

      <div style={{ borderBottom: '1px solid #ccc', margin: '10px 0' }} />

      {/* Section header */}
      <div style={{ backgroundColor: '#f3f4f6', padding: '5px 10px', fontWeight: 700, fontStyle: 'italic', fontSize: 11.5, marginBottom: 10, borderLeft: `3px solid ${BLUE}` }}>
        {SECCION_LABEL[data.tipo]}
      </div>

      {/* Content */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <Contenido data={data} />
      </div>

      {/* QR + Signature(s) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 40 }}>
        <QRBox url={data.qrUrl} />
        <FirmaPacienteBox data={data} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1.5px solid #333', width: 180, marginBottom: 4 }} />
          <div style={{ fontSize: 10, color: '#555' }}>{data.medico.nombre}</div>
          {data.medico.cedula && <div style={{ fontSize: 9.5, color: '#777' }}>Cédula: {data.medico.cedula}</div>}
        </div>
      </div>

      {/* Footer lines */}
      <div style={{ borderTop: `3px solid ${BLUE}`, marginTop: 20, marginBottom: 3 }} />
      <div style={{ borderTop: `1.5px solid ${BLUE}`, marginBottom: 8 }} />

      {/* Footer text */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#555' }}>
        <div>{data.clinica.direccion}</div>
        {data.clinica.telefono && <div>Teléfono: {data.clinica.telefono}</div>}
        <div style={{ textAlign: 'right', color: '#777' }}>Receta electrónica</div>
      </div>
    </div>
  );
}
